<!--
GatewayTestDevTrigger — dev-only console for the GatewayTest provider.

Two surfaces:
  1. Trigger — fires a GatewayTest webhook (confirm/fail) for a charge id.
  2. Settings — reads/writes the provider's behaviour knobs and rotates the
     webhook secret (the secret is rotate-only by design: the read paths only
     ever return it masked).

RETIRED KEY — `auto_confirm` (removed here 2026-07-29): the panel used to show a
fourth toggle for it. The backend adjudicated D143 by ELIMINATING the key: its
only behaviour was gating the manual trigger, M8 cut that on purpose, and what
remained was storage with no reader. The write endpoint now REFUSES it
("setting_key 'auto_confirm' is not writable via this endpoint"), the read no
longer returns it, and existing rows are dropped at boot by
Payment_gateway_settings_model::pruneRetiredKeys(). Because the save loop writes
one key per request, leaving the toggle in place broke the WHOLE save. Do not
reintroduce the field — backend guard: tests/unit/GatewayTestAutoConfirmRetiredKeyTest.php.

GATE: rendered only when the gateway feature map reports `gatewaytest` as
ENABLED on at least one feature. The enabled flag — not the provider's mere
presence in the map — is the environment signal: the backend seeds a gatewaytest
row everywhere but derives its value from AWRENV_SISTEMAAPI_GATEWAYTEST_ENABLED,
which production never defines. A build-time DEV flag cannot replace it: every
deploy (hml included) is built with `--mode production`, so that flag is false
in hml, where GatewayTest legitimately runs.

Testid CONTRACT — the four trigger identifiers below are ALSO driven by the
critical-flow smoke specs; do not rename them without updating those specs:
  fake-gateway-dev-trigger    — panel wrapper
  fake-trigger-id-input       — text field for charge_id
  fake-trigger-confirm-button — fires ONE_OFF_CHARGE_CONFIRMED
  fake-trigger-fail-button    — fires ONE_OFF_CHARGE_FAILED

Backend: POST subaccounts/webhooks/gatewaytest (raw webhook receiver, requires
an HMAC signature) + subscriptions/gateways/settings|rotate-secret.

WHY THE TRIGGER ANSWERS 401 (and why that is not a bug to "fix" here): the raw
receiver verifies an HMAC over the exact bytes it received, and the signing key
— the gatewaytest webhook_secret — is rotate-only and masked on every read, so
a browser can never hold it. An unsigned POST is therefore refused, always. The
S3b backend round already shipped the staff-authenticated door that solves this
(POST subaccounts/webhooks/gatewaytest-staff: Full-only, env-gated, signs the
caller's payload SERVER-side and re-enters the SAME verify path — not a bypass).
Repointing `fireFakeTrigger` at it was deliberately kept out of this round, so
until that rewiring lands the panel surfaces the refusal literally, in
[data-testid="gatewaytest-trigger-401-notice"], rather than reporting a generic
error or — worse — pretending the webhook fired.

COST OF THAT STATE: `subaccounts/webhooks/gatewaytest` is not in httpService's
authExcludedEndpoints, so every refused trigger burns one token refresh before
the 401 reaches this component. Harmless with a live session; with a dead one
the user lands on /login. It disappears with the rewiring.

Skill: gatewaytest-console (console, gates, settings, webhook-log filters).
-->
<template>
  <div
    v-if="isAvailable"
    class="gatewaytest-dev-trigger"
    data-testid="fake-gateway-dev-trigger"
  >
    <!-- ── Box 1: webhook trigger ────────────────────────────────────────── -->
    <box>
      <template #header>{{ t('Dev Tool — GatewayTest Trigger') }}</template>
      <p class="trigger-description">
        {{ t('Dispara webhooks do GatewayTest para testar o ciclo de confirmação/falha sem um gateway real.') }}
      </p>

      <div class="trigger-form">
        <input-text
          v-model="chargeId"
          :label="t('Charge ID')"
          elem-id="fake-trigger-id"
          data-testid="fake-trigger-id-input"
        />

        <div class="trigger-actions">
          <Button
            :label="t('Confirmar')"
            icon="pi pi-check"
            severity="success"
            :loading="isSubmitting"
            data-testid="fake-trigger-confirm-button"
            @click="handleFire('ONE_OFF_CHARGE_CONFIRMED')"
          />
          <Button
            :label="t('Falhar')"
            icon="pi pi-times"
            severity="danger"
            :loading="isSubmitting"
            data-testid="fake-trigger-fail-button"
            @click="handleFire('ONE_OFF_CHARGE_FAILED')"
          />
        </div>

        <inline-message
          v-if="lastResult && lastResult.kind === 'unsigned'"
          severity="warn"
          data-testid="gatewaytest-trigger-401-notice"
        >
          {{ lastResult.message }}
        </inline-message>
        <inline-message
          v-else-if="lastResult"
          :severity="lastResult.kind === 'ok' ? 'success' : 'error'"
          data-testid="gatewaytest-trigger-result"
        >
          {{ lastResult.message }}
        </inline-message>
      </div>
    </box>

    <!-- ── Box 2: provider settings ──────────────────────────────────────── -->
    <box data-testid="gatewaytest-settings-box" class="settings-box">
      <template #header>{{ t('Configuração do GatewayTest') }}</template>

      <div v-if="settingsLoading" class="settings-loading">
        <ProgressSpinner />
      </div>

      <div v-else class="settings-form">
        <p class="settings-intro">
          {{ t('Cada chave imita um comportamento real de gateway. O padrão de todas é "desligado": o simulador aprova, confirma na hora e cancela sem reclamar.') }}
        </p>

        <div class="setting-field">
          <input-toggle
            v-model="form.never_confirm"
            :label="t('Nunca confirmar')"
            elem-id="gatewaytest-never-confirm"
            data-testid="gatewaytest-setting-never_confirm"
          />
          <p class="setting-hint" data-testid="gatewaytest-hint-never_confirm">
            {{ t('Ligado: todo evento de confirmação é ignorado, inclusive os disparados à mão aqui em cima. A cobrança nasce e nunca é paga — é assim que se leva uma assinatura até o vencimento e a carência.') }}
          </p>
        </div>

        <div class="setting-field">
          <input-number
            v-model="form.failure_rate"
            :label="t('Taxa de falha (%)')"
            :min="0"
            :max="100"
            elem-id="gatewaytest-failure-rate"
            data-testid="gatewaytest-setting-failure_rate"
          />
          <p class="setting-hint" data-testid="gatewaytest-hint-failure_rate">
            {{ t('Chance de a CRIAÇÃO de uma assinatura ou de uma compra avulsa ser recusada, como uma autorização negada. Sorteado a cada tentativa: 100 recusa sempre, 0 nunca. Não afeta confirmação nem cancelamento.') }}
          </p>
        </div>

        <div class="setting-field">
          <input-number
            v-model="form.confirm_delay_seconds"
            :label="t('Atraso de confirmação (s)')"
            :min="0"
            elem-id="gatewaytest-confirm-delay"
            data-testid="gatewaytest-setting-confirm_delay_seconds"
          />
          <p class="setting-hint" data-testid="gatewaytest-hint-confirm_delay_seconds">
            {{ t('Janela mínima de liquidação: uma confirmação cujo pagamento aconteceu há menos segundos que este número é ignorada por prematura. Imita o dinheiro que não cai no mesmo instante. 0 nunca bloqueia.') }}
          </p>
        </div>

        <div class="setting-field">
          <input-toggle
            v-model="form.cancel_always_fails"
            :label="t('Cancelamento sempre falha')"
            elem-id="gatewaytest-cancel-always-fails"
            data-testid="gatewaytest-setting-cancel_always_fails"
          />
          <p class="setting-hint" data-testid="gatewaytest-hint-cancel_always_fails">
            {{ t('Ligado: o gateway recusa todo pedido de cancelamento. Serve para provar que o sistema cancela primeiro no gateway e só depois localmente — com a recusa ligada, a assinatura tem de continuar exatamente como estava.') }}
          </p>
        </div>

        <div class="setting-field">
          <input-toggle
            v-model="form.retry_pending"
            :label="t('Retentativa ainda agendada')"
            elem-id="gatewaytest-retry-pending"
            data-testid="gatewaytest-setting-retry_pending"
          />
          <p class="setting-hint" data-testid="gatewaytest-hint-retry_pending">
            {{ t('Ligado: o gateway responde que ainda vai tentar cobrar de novo, e o corte do fim da carência é adiado em vez de executado. Imita a régua de retentativas, que mora no painel do gateway e não na nossa configuração — por isso perguntamos na hora de cortar, em vez de confiar no que está salvo aqui.') }}
          </p>
        </div>

        <div class="settings-actions">
          <Button
            :label="t('Salvar configurações')"
            icon="pi pi-save"
            :loading="isSavingSettings"
            data-testid="gatewaytest-settings-save-button"
            @click="handleSaveSettings"
          />
        </div>

        <div class="secret-row">
          <span class="secret-label">{{ t('Segredo do webhook') }}:</span>
          <span class="secret-value" data-testid="gatewaytest-secret-masked">{{ maskedSecret || '—' }}</span>
          <Button
            :label="t('Rotacionar segredo')"
            icon="pi pi-refresh"
            severity="warn"
            :loading="isRotating"
            data-testid="gatewaytest-rotate-secret-button"
            @click="handleRotate"
          />
        </div>

        <inline-message
          v-if="rotatedSecret"
          severity="warn"
          data-testid="gatewaytest-rotated-secret"
        >
          {{ rotatedSecret }} — {{ t('Copie agora — este valor não será exibido novamente.') }}
        </inline-message>
      </div>
    </box>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { eventBus } from '../../../eventBus'
import { log } from '../../../utils/DebugLogger'
import { usePaymentGatewayRegistry } from '../../../composables/usePaymentGatewayRegistry'

const GATEWAYTEST = 'gatewaytest'

const { t } = useI18n()
const {
  featureMap,
  fireFakeTrigger,
  gatewaySettings,
  settingsLoading,
  loadGatewaySettings,
  saveGatewaySetting,
  rotateGatewaySecret,
} = usePaymentGatewayRegistry()

// ── Availability gate ───────────────────────────────────────────────────────
// PRESENCE IS NOT THE SIGNAL. The backend seeds a gatewaytest row for every
// feature unconditionally (Payment_gateway_features_model::seedIfEmpty) and the
// map endpoint emits every row it finds, so `gatewaytest` is present in the map
// in production too — only its `enabled` flag differs. That flag IS the dev
// switch: seedProviders() derives it from AWRENV_SISTEMAAPI_GATEWAYTEST_ENABLED,
// which production never defines (GatewayTestEnvironment fails closed). So the
// console gates on the flag, on any feature.
const isAvailable = computed<boolean>(() =>
  !!featureMap.value
  && Object.values(featureMap.value).some(providerMap => providerMap[GATEWAYTEST]?.enabled === true),
)

// ── Trigger state ───────────────────────────────────────────────────────────

const chargeId = ref('')
const isSubmitting = ref(false)
const lastResult = ref<{ kind: 'ok' | 'unsigned' | 'error'; message: string } | null>(null)

const UNSIGNED_MESSAGE = t('Disparo recusado (401): o webhook do GatewayTest exige assinatura HMAC. Aguardando religação do disparo assinado (S3b) — o painel ainda não assina o corpo.')

/**
 * handleFire — fires a GatewayTest webhook event for the typed charge id.
 *
 * The endpoint requires an HMAC signature that this panel does not yet produce,
 * so a 401 is the expected answer until the signed trigger is rewired (S3b).
 * That case is reported honestly instead of being folded into a generic error.
 */
async function handleFire(eventType: 'ONE_OFF_CHARGE_CONFIRMED' | 'ONE_OFF_CHARGE_FAILED'): Promise<void> {
  if (!chargeId.value.trim()) {
    eventBus.emit('globalMessage.error', t('Informe o Charge ID antes de disparar o webhook.'))
    return
  }

  isSubmitting.value = true
  lastResult.value = null
  try {
    await fireFakeTrigger(eventType, chargeId.value.trim())
    lastResult.value = { kind: 'ok', message: t('Webhook disparado com sucesso.') }
    log('GatewayTestDevTrigger', 'handleFire', 'Webhook fired', { eventType })
  } catch (err) {
    // The rejected error carries `.status`; typed structurally rather than by
    // importing the transport type, which a Vue domain component must not do.
    const status = (err as { status?: number })?.status
    lastResult.value = status === 401
      ? { kind: 'unsigned', message: UNSIGNED_MESSAGE }
      : { kind: 'error', message: t('Erro ao disparar webhook do GatewayTest.') }
    log('GatewayTestDevTrigger', 'handleFire', 'Erro ao disparar webhook', { status, eventType })
  } finally {
    isSubmitting.value = false
  }
}

// ── Settings state ──────────────────────────────────────────────────────────

const form = reactive<{
  never_confirm: boolean
  failure_rate: number | null
  confirm_delay_seconds: number | null
  /** S12/M2b — deterministic sad path for cancelSubscription(). */
  cancel_always_fails: boolean
  /** S12/M3 — makes the runtime retry guard defer the end-of-carência cut. */
  retry_pending: boolean
}>({
  never_confirm: false,
  failure_rate: 0,
  confirm_delay_seconds: 0,
  cancel_always_fails: false,
  retry_pending: false,
})

const isSavingSettings = ref(false)
const isRotating = ref(false)
const rotatedSecret = ref('')

const maskedSecret = computed<string>(
  () => gatewaySettings.value?.find(row => row.setting_key === 'webhook_secret')?.setting_value ?? '',
)

function settingValue(key: string, fallback: string): string {
  return gatewaySettings.value?.find(row => row.setting_key === key)?.setting_value ?? fallback
}

// CANONICAL HYDRATION (user decision 2026-08-12): the form must ALWAYS show
// what is stored — an ON knob loads ON, an OFF knob loads OFF. Two rules keep
// that true:
//   1. `immediate: true` — gatewaySettings is MODULE-scope state shared across
//      SPA navigations; on a REMOUNT it is already populated, so a
//      change-only watcher never fired and the form silently stayed at its
//      defaults. Saving then wrote those defaults over the real values — the
//      exact "toggle shows OFF but behaves ON" incident this fixes.
//   2. every mount RELOADS from the server (below) — the in-memory copy may
//      predate a save made elsewhere; stale state must not survive a revisit.
watch(gatewaySettings, rows => {
  if (!rows) return
  form.never_confirm = settingValue('never_confirm', '0') === '1'
  form.failure_rate = Number(settingValue('failure_rate', '0'))
  form.confirm_delay_seconds = Number(settingValue('confirm_delay_seconds', '0'))
  form.cancel_always_fails = settingValue('cancel_always_fails', '0') === '1'
  form.retry_pending = settingValue('retry_pending', '0') === '1'
}, { immediate: true })

// Fetch fresh once per MOUNT (not once per SPA session) as soon as the panel
// is available — see the canonical-hydration note above.
const fetchedThisMount = ref(false)
watch(isAvailable, available => {
  if (!available || fetchedThisMount.value) return
  fetchedThisMount.value = true
  void loadGatewaySettings(GATEWAYTEST).catch((err: unknown) => {
    log('GatewayTestDevTrigger', 'loadSettings', 'Erro ao carregar configurações', {
      message: (err as Error)?.message,
    })
    eventBus.emit('globalMessage.error', t('Erro ao carregar configurações do GatewayTest.'))
  })
}, { immediate: true })

/**
 * handleSaveSettings — the endpoint writes ONE key per call, so every knob is
 * posted sequentially. Values go on the wire as strings (the backend reads
 * '1'/'0' for the flags and integers for the numbers).
 *
 * `auto_confirm` is NOT among them — the backend retired the key and now refuses
 * the write (see the retired-key note in the file header). Since one refusal
 * aborts the whole sequence, sending it would take the other knobs down with it.
 *
 * The values are read from a SNAPSHOT taken before the first request. The
 * form is hydrated from `gatewaySettings` by a watcher, so reading `form` live
 * across the awaits would let any refresh of that ref mid-loop feed pre-edit
 * values back into the requests still to be sent. One reload at the end brings
 * the form back in sync with what the server actually stored.
 */
async function handleSaveSettings(): Promise<void> {
  const snapshot = {
    never_confirm: form.never_confirm ? '1' : '0',
    failure_rate: String(form.failure_rate ?? 0),
    confirm_delay_seconds: String(form.confirm_delay_seconds ?? 0),
    cancel_always_fails: form.cancel_always_fails ? '1' : '0',
    retry_pending: form.retry_pending ? '1' : '0',
  }

  isSavingSettings.value = true
  try {
    await saveGatewaySetting(GATEWAYTEST, 'never_confirm', snapshot.never_confirm)
    await saveGatewaySetting(GATEWAYTEST, 'failure_rate', snapshot.failure_rate)
    await saveGatewaySetting(GATEWAYTEST, 'confirm_delay_seconds', snapshot.confirm_delay_seconds)
    await saveGatewaySetting(GATEWAYTEST, 'cancel_always_fails', snapshot.cancel_always_fails)
    await saveGatewaySetting(GATEWAYTEST, 'retry_pending', snapshot.retry_pending)
    await loadGatewaySettings(GATEWAYTEST)
    eventBus.emit('globalMessage.success', t('Configurações do GatewayTest salvas.'))
    log('GatewayTestDevTrigger', 'handleSaveSettings', 'Settings saved')
  } catch (err) {
    log('GatewayTestDevTrigger', 'handleSaveSettings', 'Erro ao salvar configurações', {
      message: (err as Error)?.message,
    })
    eventBus.emit('globalMessage.error', t('Erro ao salvar configurações do GatewayTest.'))
  } finally {
    isSavingSettings.value = false
  }
}

/**
 * handleRotate — rotates the webhook secret. The backend returns the new value
 * unmasked exactly once, so it is surfaced here with a copy-now warning; the
 * subsequent reload puts the masked form back on screen.
 */
async function handleRotate(): Promise<void> {
  if (!window.confirm(t('Rotacionar o segredo invalida o segredo atual do GatewayTest. Continuar?'))) return

  isRotating.value = true
  try {
    rotatedSecret.value = await rotateGatewaySecret(GATEWAYTEST)
    log('GatewayTestDevTrigger', 'handleRotate', 'Secret rotated')
  } catch (err) {
    log('GatewayTestDevTrigger', 'handleRotate', 'Erro ao rotacionar segredo', {
      message: (err as Error)?.message,
    })
    eventBus.emit('globalMessage.error', t('Erro ao rotacionar o segredo do GatewayTest.'))
  } finally {
    isRotating.value = false
  }
}
</script>

<style scoped>
.gatewaytest-dev-trigger {
  margin-top: 24px;
}

.trigger-description {
  margin: 0 0 16px 0;
  color: #666;
  font-size: 0.875rem;
}

.trigger-form,
.settings-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Each knob is control + explanation. The gap is tighter than the form's so the
   hint reads as belonging to the control above it, not as a sibling field. */
.setting-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.settings-intro,
.setting-hint {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.45;
  color: var(--text-color-secondary, #6c757d);
  max-width: 68ch;
}

.settings-intro {
  margin-bottom: 4px;
}

.trigger-actions,
.settings-actions {
  display: flex;
  gap: 8px;
}

.settings-box {
  margin-top: 16px;
}

.settings-loading {
  display: flex;
  justify-content: center;
  padding: 24px;
}

.secret-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.875rem;
}

.secret-label {
  color: #666;
}

.secret-value {
  font-family: monospace;
  color: #111827;
}
</style>
